import {
	Button,
	DialogUntitledUI,
	Modal,
	ModalBackgroundCircle,
	ModalOverlay,
} from "@components";
import { XClose } from "@untitledui/icons";
import type { ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

type ModalActionVariant = "primary" | "secondary" | "danger";

type ConfirmOptions = {
	title?: string;
	description?: string;
	content?: ReactNode;
	confirmText?: string;
	cancelText?: string;
	confirmVariant?: ModalActionVariant;
	cancelVariant?: ModalActionVariant;
	variant?: "success" | "error" | "warning" | "info";
};

type PendingConfirm = {
	options: ConfirmOptions;
	resolve: (confirmed: boolean) => void;
};

type ModalContextValue = {
	openConfirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

type ModalProviderProps = {
	children: ReactNode;
};

function toButtonVariant(
	v: ModalActionVariant
): "default" | "secondary" | "destructive" {
	if (v === "danger") {
		return "destructive";
	}
	if (v === "primary") {
		return "default";
	}
	return "secondary";
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
	const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
		null
	);

	const close = useCallback(
		(result: boolean) => {
			if (pendingConfirm) {
				pendingConfirm.resolve(result);
				setPendingConfirm(null);
			}
		},
		[pendingConfirm]
	);

	const openConfirm = useCallback(
		(options: ConfirmOptions) =>
			new Promise<boolean>((resolve) => {
				setPendingConfirm({ options, resolve });
			}),
		[]
	);

	const value = useMemo(
		() => ({
			openConfirm,
		}),
		[openConfirm]
	);

	const isOpen = !!pendingConfirm;
	const {
		title,
		description,
		content,
		confirmText = "Confirmar",
		cancelText = "Cancelar",
		confirmVariant = "primary",
		cancelVariant = "secondary",
		variant = "success",
	} = pendingConfirm?.options || {};

	let confirmModal: ReactNode = null;
	if (isOpen) {
		confirmModal = (
			<ModalOverlay
				isOpen={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						close(false);
					}
				}}
			>
				<Modal className="relative mx-auto h-fit overflow-hidden rounded-2xl bg-bg-primary">
					<ModalBackgroundCircle variant={variant} />
					<Button
						className="absolute top-3 right-3 z-10 size-11 p-2 text-fg-secondary"
						onClick={() => close(true)}
						type="button"
						variant="ghost"
					>
						<XClose className="size-6 text-fg-quaternary" />
					</Button>

					<DialogUntitledUI
						aria-label="Modal de confirmação"
						className="relative z-auto mt-[88px] flex max-w-md flex-col items-start p-6 pt-0 shadow-lg"
						role="alertdialog"
					>
						{!!title && (
							<h2 className="font-semibold text-fg-primary text-md leading-6">
								{title}
							</h2>
						)}
						{!!description && (
							<p className="mt-0.5 text-fg-tertiary text-sm leading-5">
								{description}
							</p>
						)}
						{!!content && (
							<div className="text-fg-secondary text-sm">{content}</div>
						)}
						<div className="mt-8 flex w-full justify-end gap-3">
							<Button
								onClick={() => close(false)}
								size="default"
								type="button"
								variant={toButtonVariant(cancelVariant)}
							>
								{cancelText}
							</Button>
							<Button
								onClick={() => close(true)}
								size="default"
								type="button"
								variant={toButtonVariant(confirmVariant)}
							>
								{confirmText}
							</Button>
						</div>
					</DialogUntitledUI>
				</Modal>
			</ModalOverlay>
		);
	}

	return (
		<ModalContext.Provider value={value}>
			{children}
			{confirmModal}
		</ModalContext.Provider>
	);
};

export const useConfirmModal = () => {
	const ctx = useContext(ModalContext);
	if (!ctx) {
		throw new Error("useConfirmModal must be used within a ModalProvider");
	}

	const confirm = useCallback(
		(options: ConfirmOptions) => ctx.openConfirm(options),
		[ctx]
	);

	return { confirm };
};
